import { createSlice } from '@reduxjs/toolkit';

const THEME_KEY = 'theme';
const PRIMARY_COLOR_KEY = 'primaryColor';
const SURFACE_TONE_KEY = 'surfaceTone';
const TEXT_TONE_KEY = 'textTone';
const HIDE_HEADER_KEY = 'hideHeader';
const COMPACT_HEADER_CONTROLS_KEY = 'compactHeaderControls';

const PRIMARY_COLORS = ['white', 'green', 'yellow', 'blue', 'cyan', 'purple', 'orange', 'pink', 'red'];
const SURFACE_TONES = [
    'surfaceLight0',
    'surfaceLight1',
    'surfaceLight2',
    'surfaceLight3',
    'surfaceLight4',
    'surfaceMid0',
    'surfaceMid1',
    'surfaceDark0',
    'surfaceDark1',
    'surfaceDark2',
];
const TEXT_TONES = ['textDark0', 'textDark1', 'textLight0', 'textLight1', 'textMuted0', 'textMuted1'];

function readStoredPrimaryColor() {
    if (typeof window === 'undefined') return 'green';
    try {
        const v = localStorage.getItem(PRIMARY_COLOR_KEY);
        return PRIMARY_COLORS.includes(v) ? v : 'green';
    } catch {
        return 'green';
    }
}

function persistPrimaryColor(primaryColor) {
    if (typeof localStorage === 'undefined') return;
    try {
        localStorage.setItem(PRIMARY_COLOR_KEY, primaryColor);
    } catch {
        /* ignore */
    }
}

function readStoredSurfaceTone() {
    if (typeof window === 'undefined') return 'surfaceLight1';
    try {
        const v = localStorage.getItem(SURFACE_TONE_KEY);
        return SURFACE_TONES.includes(v) ? v : 'surfaceLight1';
    } catch {
        return 'surfaceLight1';
    }
}

function persistSurfaceTone(surfaceTone) {
    if (typeof localStorage === 'undefined') return;
    try {
        localStorage.setItem(SURFACE_TONE_KEY, surfaceTone);
    } catch {
        /* ignore */
    }
}

function readStoredTextTone() {
    if (typeof window === 'undefined') return 'textDark0';
    try {
        const v = localStorage.getItem(TEXT_TONE_KEY);
        return TEXT_TONES.includes(v) ? v : 'textDark0';
    } catch {
        return 'textDark0';
    }
}

function persistTextTone(textTone) {
    if (typeof localStorage === 'undefined') return;
    try {
        localStorage.setItem(TEXT_TONE_KEY, textTone);
    } catch {
        /* ignore */
    }
}

function readStoredHideHeader() {
    if (typeof window === 'undefined') return false;
    try {
        const v = localStorage.getItem(HIDE_HEADER_KEY);
        return v === 'true';
    } catch {
        return false;
    }
}

function persistHideHeader(hideHeader) {
    if (typeof localStorage === 'undefined') return;
    try {
        localStorage.setItem(HIDE_HEADER_KEY, String(Boolean(hideHeader)));
    } catch {
        /* ignore */
    }
}

function readStoredCompactHeaderControls() {
    if (typeof window === 'undefined') return false;
    try {
        const v = localStorage.getItem(COMPACT_HEADER_CONTROLS_KEY);
        return v === 'true';
    } catch {
        return false;
    }
}

function persistCompactHeaderControls(compact) {
    if (typeof localStorage === 'undefined') return;
    try {
        localStorage.setItem(COMPACT_HEADER_CONTROLS_KEY, String(Boolean(compact)));
    } catch {
        /* ignore */
    }
}

/**
 * Light is the default appearance. Only an explicit stored choice switches to
 * dark, which matches the pre-hydration script in index.html (it adds the `dark`
 * class only when localStorage says 'dark'), so there is no flash on first load.
 */
function readStoredTheme() {
    if (typeof window === 'undefined') return 'light';
    try {
        const v = localStorage.getItem(THEME_KEY);
        return v === 'dark' || v === 'light' ? v : 'light';
    } catch {
        return 'light';
    }
}

function persistTheme(theme) {
    if (typeof localStorage === 'undefined') return;
    try {
        localStorage.setItem(THEME_KEY, theme);
    } catch {
        /* ignore */
    }
}

const initialState = {
    theme: readStoredTheme(), // 'light' | 'dark'
    primaryColor: readStoredPrimaryColor(),
    surfaceTone: readStoredSurfaceTone(),
    textTone: readStoredTextTone(),
    hideHeader: readStoredHideHeader(),
    compactHeaderControls: readStoredCompactHeaderControls(),
    sidebarOpen: true,
    sidebarCollapsed: false,
    modalStack: [], // Supports stacked modals
};

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        setTheme(state, action) {
            const next = action.payload === 'dark' ? 'dark' : 'light';
            state.theme = next;
            persistTheme(next);
        },
        toggleTheme(state) {
            state.theme = state.theme === 'dark' ? 'light' : 'dark';
            persistTheme(state.theme);
        },
        setPrimaryColor(state, action) {
            const next = PRIMARY_COLORS.includes(action.payload) ? action.payload : 'green';
            state.primaryColor = next;
            persistPrimaryColor(next);
        },
        setSurfaceTone(state, action) {
            const next = SURFACE_TONES.includes(action.payload) ? action.payload : 'surfaceLight1';
            state.surfaceTone = next;
            persistSurfaceTone(next);
        },
        setTextTone(state, action) {
            const next = TEXT_TONES.includes(action.payload) ? action.payload : 'textDark0';
            state.textTone = next;
            persistTextTone(next);
        },
        toggleHideHeader(state) {
            state.hideHeader = !state.hideHeader;
            persistHideHeader(state.hideHeader);
        },
        setHideHeader(state, action) {
            state.hideHeader = Boolean(action.payload);
            persistHideHeader(state.hideHeader);
        },
        toggleCompactHeaderControls(state) {
            state.compactHeaderControls = !state.compactHeaderControls;
            persistCompactHeaderControls(state.compactHeaderControls);
        },
        setCompactHeaderControls(state, action) {
            state.compactHeaderControls = Boolean(action.payload);
            persistCompactHeaderControls(state.compactHeaderControls);
        },
        toggleSidebar(state) {
            state.sidebarOpen = !state.sidebarOpen;
        },
        setSidebarOpen(state, action) {
            state.sidebarOpen = action.payload;
        },
        toggleSidebarCollapsed(state) {
            state.sidebarCollapsed = !state.sidebarCollapsed;
        },
        openModal(state, action) {
            state.modalStack.push(action.payload);
        },
        closeModal(state) {
            state.modalStack.pop();
        },
        closeAllModals(state) {
            state.modalStack = [];
        },
    },
});

export const {
    setTheme,
    toggleTheme,
    setPrimaryColor,
    setSurfaceTone,
    setTextTone,
    toggleHideHeader,
    setHideHeader,
    toggleCompactHeaderControls,
    setCompactHeaderControls,
    toggleSidebar,
    setSidebarOpen,
    toggleSidebarCollapsed,
    openModal,
    closeModal,
    closeAllModals,
} = uiSlice.actions;

export const uiReducer = uiSlice.reducer;

// ─── Selectors ──────────────────────────────────────────────────────────────
export const selectTheme = (state) => state.ui.theme;
export const selectPrimaryColor = (state) => state.ui.primaryColor;
export const selectSurfaceTone = (state) => state.ui.surfaceTone;
export const selectTextTone = (state) => state.ui.textTone;
export const selectHideHeader = (state) => state.ui.hideHeader;
export const selectCompactHeaderControls = (state) => state.ui.compactHeaderControls;
export const selectSidebarOpen = (state) => state.ui.sidebarOpen;
export const selectSidebarCollapsed = (state) => state.ui.sidebarCollapsed;
export const selectActiveModal = (state) => state.ui.modalStack[state.ui.modalStack.length - 1] || null;
export const selectIsModalOpen = (state) => state.ui.modalStack.length > 0;
